/**
 * PaginationBar molecule — prop-based list/table footer pagination.
 * Structure: summary (+ optional page size) + atoms/Pagination controls.
 * Mobile: stacked. Laptop+: summary left, controls flush right.
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "../../atoms/Typography";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPageList,
  PaginationPrevious,
} from "../../atoms/Pagination";
import {
  getPaginationBarClassName,
  getPaginationBarControlsClassName,
  getPaginationBarControlsWrapClassName,
  getPaginationBarMetaClassName,
  getPaginationBarPageSizeClassName,
  getPaginationBarSummaryClassName,
} from "./PaginationBar.styles";
import type { PaginationBarProps } from "./PaginationBar.types";

const DEFAULT_PAGE_SIZES = [50, 100, 150, 200];

export type { PaginationBarProps } from "./PaginationBar.types";

export const PaginationBar: React.FC<PaginationBarProps> = ({
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  className,
  itemLabel = "items",
  summary = "pageOf",
  itemsPerPage,
  onItemsPerPageChange,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  hideWhenSinglePage = true,
  disabled = false,
}) => {
  const { t } = useTranslation("common");

  if (hideWhenSinglePage && totalPages <= 1) return null;

  const safeTotalPages = Math.max(1, totalPages);
  const safePage = Math.min(Math.max(1, currentPage), safeTotalPages);
  const showPageSize = itemsPerPage != null && onItemsPerPageChange != null;
  const startItem =
    totalItems === 0 ? 0 : (safePage - 1) * (itemsPerPage ?? totalItems) + 1;
  const endItem = Math.min(
    safePage * (itemsPerPage ?? totalItems),
    totalItems
  );

  const summaryText =
    summary === "showing"
      ? t("pagination.showing", {
          from: startItem,
          to: endItem,
          total: totalItems,
        })
      : t("pagination.page_of", {
          current: safePage,
          total: safeTotalPages,
          count: totalItems,
          label: itemLabel,
        });

  return (
    <div className={getPaginationBarClassName(className)}>
      <div className={getPaginationBarMetaClassName()}>
        {showPageSize && (
          <div className="flex items-center gap-2">
            <Typography as="span" variant="body-xs" color="secondary">
              {t("pagination.show")}
            </Typography>
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              disabled={disabled}
              className={getPaginationBarPageSizeClassName()}
              aria-label={t("pagination.per_page")}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <Typography as="span" variant="body-xs" color="secondary">
              {t("pagination.per_page")}
            </Typography>
          </div>
        )}
        <Typography
          as="p"
          variant="body-xs"
          color="muted"
          className={getPaginationBarSummaryClassName()}
          aria-live="polite"
        >
          {summaryText}
        </Typography>
      </div>

      <div className={getPaginationBarControlsWrapClassName()}>
        <Pagination className={getPaginationBarControlsClassName()}>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => onPageChange(Math.max(1, safePage - 1))}
                disabled={disabled || safePage <= 1}
              />
            </PaginationItem>
            <PaginationPageList
              currentPage={safePage}
              totalPages={safeTotalPages}
              onPageChange={onPageChange}
            />
            <PaginationItem>
              <PaginationNext
                onClick={() =>
                  onPageChange(Math.min(safeTotalPages, safePage + 1))
                }
                disabled={disabled || safePage >= safeTotalPages}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  );
};
